'use client'

import { motion } from 'framer-motion'
import { Space_Grotesk, Outfit } from 'next/font/google'
import { BackgroundBeams } from '../../_components/_ui/background-beams'

const spaceGrotesk = Space_Grotesk({ 
  subsets: ['latin'],
  variable: '--font-space-grotesk',
  display: 'swap'
})

const outfit = Outfit({ 
  subsets: ['latin'],
  variable: '--font-outfit',
  display: 'swap'
})

const Terms = () => {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10">
                {/* Hero Section */}
                <div className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-2xl sm:text-3xl font-bold text-white leading-tight"
                        >
                            Terms of Service
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-sm text-gray-400 mt-2"
                        >
                            Effective Date: {new Date().toLocaleDateString()}
                        </motion.p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-8 border border-neutral-800 prose prose-invert max-w-none"
                    >
                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">1. AGREEMENT TO TERMS</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                1.1. By accessing or using the Autofoc.us service ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you do not have permission to access the Service.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">2. SERVICE DESCRIPTION</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                2.1. Autofoc.us provides a platform for organizing and sharing content through external links and references. The Service acts primarily as an intermediary and does not host user-generated content directly.
                            </p>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                2.2. Users acknowledge that:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>The Service primarily manages links to external content</li>
                                <li>Content availability depends on third-party services</li>
                                <li>Autofoc.us is not responsible for external content accessibility</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">3. USER OBLIGATIONS</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                3.1. Account Requirements:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2 mb-4">
                                <li>Users must be at least 13 years of age</li>
                                <li>Accurate registration information must be provided</li>
                                <li>Account credentials must be maintained securely</li>
                                <li>One person or entity may maintain only one account</li>
                            </ul>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                3.2. Users are solely responsible for maintaining their account security and all activities conducted through their account.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">4. INTELLECTUAL PROPERTY</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                4.1. Service Rights:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2 mb-4">
                                <li>The Service, including its original features and functionality, remains the exclusive property of Autofoc.us LLC</li>
                                <li>No right or license is granted to use any trademark without prior written permission</li>
                            </ul>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                4.2. User Content Rights: Users retain all rights to their content while granting Autofoc.us a limited license to display and manage associated links.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">5. PROHIBITED ACTIVITIES</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                Users shall not:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>Violate any applicable laws or regulations</li>
                                <li>Infringe upon intellectual property rights</li>
                                <li>Attempt to circumvent security measures</li>
                                <li>Engage in unauthorized automated access</li>
                                <li>Share malicious content or links</li>
                                <li>Harass, abuse, or harm others</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">6. TERMINATION</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                6.1. Autofoc.us reserves the right to:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>Terminate or suspend accounts without prior notice</li>
                                <li>Remove or disable access to any content or links</li>
                                <li>Take appropriate legal action for violations</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">7. LIMITATION OF LIABILITY</h2>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                7.1. TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUTOFOC.US AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">8. SERVICE CONTINUITY</h2>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                8.1. In the event of service discontinuation:
                            </p>
                            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-2">
                                <li>Users will receive 30 days advance notice</li>
                                <li>The service code may be open-sourced</li>
                                <li>Users will be provided tools to export their data</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-white mb-4">9. GOVERNING LAW</h2>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-4">10. CONTACT</h2>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                For legal inquiries regarding these Terms, contact:<br />
                                Email: legal@autofoc.us
                            </p>
                        </section>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default Terms 